import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { DateField, MoneyField, TextField } from '../../../ui/fields';
import { Field, FormModal, FormSection, V } from '../../../ui/forms';
import {
  Alert,
  Button,
  Card,
  Empty,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DollarOutlined, FileAddOutlined } from '@ant-design/icons';
import type { InvoiceRow, PaymentMethod, ReceiptRow } from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin surface — Collections. Real receipts today + open-invoice picker + record
 * receipt modal. "Take a payment" opens a two-step flow: pick an open invoice,
 * then submit an amount + method. Receipt number is auto-issued by the server
 * (RCP-YYYY-######) and cannot be edited (M-01).
 */
export function CollectionsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [payFor, setPayFor] = useState<InvoiceRow | null>(null);
  const [genOpen, setGenOpen] = useState(false);

  const receiptsToday = useQuery({
    queryKey: ['receipts', institution, today],
    queryFn: () => productApi.listReceipts(institution, { from: today, to: today }),
    enabled: !!institution,
  });
  const openInvoices = useQuery({
    queryKey: ['invoices', institution, { status: 'open' }],
    queryFn: () => productApi.listInvoices(institution, { status: 'open' }),
    enabled: !!institution,
  });

  const totalToday = receiptsToday.data?.total_paise ?? 0;
  const countToday = receiptsToday.data?.total ?? 0;
  const totalOpen = openInvoices.data?.total_open_paise ?? 0;
  const openCount = openInvoices.data?.total ?? 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Collections</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Real receipts. Numbers monotonic per institution (RCP-YYYY-######). Corrections never edit — reversal receipts land with the Fees module extension.
          </Typography.Text>
        </div>
        <Space>
          <Button icon={<FileAddOutlined />} onClick={() => setGenOpen(true)}>
            Generate invoices
          </Button>
        </Space>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Today" value={`₹${paise(totalToday)}`} suffix={<Typography.Text type="secondary" style={{ fontSize: 11 }}>{countToday} receipt{countToday === 1 ? '' : 's'}</Typography.Text>} /></Card>
        <Card size="small"><Statistic title="Open invoices" value={openCount} /></Card>
        <Card size="small"><Statistic title="Outstanding" value={`₹${paise(totalOpen)}`} /></Card>
        <Card size="small">
          <Statistic
            title="Take payment"
            value=""
            formatter={() => (
              <Button type="primary" icon={<DollarOutlined />} disabled={openCount === 0} onClick={() => {
                // opens picker below via focusing the open-invoices card
                const first = openInvoices.data?.rows[0];
                if (first) setPayFor(first);
              }}>
                {openCount === 0 ? 'No open invoices' : 'Record receipt'}
              </Button>
            )}
          />
        </Card>
      </div>

      <Card size="small" title={`Today's receipts (${countToday})`}>
        {receiptsToday.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {receiptsToday.isError && <Alert type="error" showIcon message="Could not load receipts" />}
        {receiptsToday.data && receiptsToday.data.rows.length === 0 && (
          <Empty description="No receipts today yet." style={{ padding: 20 }} />
        )}
        {receiptsToday.data && receiptsToday.data.rows.length > 0 && (
          <Table<ReceiptRow>
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={receiptsToday.data.rows}
            columns={[
              { title: 'Receipt no.', dataIndex: 'receipt_number', key: 'rn', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Student', key: 's', render: (_, r) => <><b>{r.student_name}</b> <Typography.Text type="secondary" style={{ fontSize: 12 }}>· {r.admission_number}</Typography.Text></> },
              { title: 'Head', dataIndex: 'fee_head_name', key: 'h' },
              { title: 'Amount', dataIndex: 'amount_paise', key: 'a', align: 'right', render: (v: number) => <b>₹{paise(v)}</b> },
              { title: 'Method', dataIndex: 'method', key: 'm', render: (v: PaymentMethod) => <Tag>{v}</Tag> },
            ]}
          />
        )}
      </Card>

      <Card size="small" title={`Open invoices (${openCount})`} extra={<Typography.Text type="secondary" style={{ fontSize: 12 }}>Click a row to record a receipt</Typography.Text>}>
        {openInvoices.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {openInvoices.data && openInvoices.data.rows.length === 0 ? (
          <Empty
            description={<span>No open invoices. Generate them from <a href={`/i/${institution}/admin/fees/structures`}>Fee structures</a>.</span>}
            style={{ padding: 20 }}
          />
        ) : (
          <Table<InvoiceRow>
            size="small"
            pagination={{ pageSize: 12 }}
            rowKey="id"
            dataSource={openInvoices.data?.rows ?? []}
            onRow={(r) => ({ onClick: () => setPayFor(r), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'Student', key: 's', render: (_, r) => <><b>{r.student_name}</b> <Typography.Text type="secondary" style={{ fontSize: 12 }}>· {r.admission_number} · {r.grade}{r.section ? `-${r.section}` : ''}</Typography.Text></> },
              { title: 'Head', dataIndex: 'fee_head_name', key: 'h' },
              { title: 'Owed', key: 'o', align: 'right', render: (_, r) => <b>₹{paise(r.amount_paise - r.paid_paise)}</b> },
              { title: 'Due', dataIndex: 'due_on', key: 'due', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Overdue', dataIndex: 'days_overdue', key: 'd', render: (v: number) => v > 0 ? <Tag color="error">{v}d</Tag> : <Tag color="success">on time</Tag> },
            ]}
          />
        )}
      </Card>

      <RecordReceiptModal
        invoice={payFor}
        institution={institution}
        onClose={() => setPayFor(null)}
        onDone={() => {
          setPayFor(null);
          void qc.invalidateQueries({ queryKey: ['receipts', institution] });
          void qc.invalidateQueries({ queryKey: ['invoices', institution] });
          void qc.invalidateQueries({ queryKey: ['briefing', institution] });
          void message.success('Receipt recorded');
        }}
      />
      <GenerateInvoicesModal
        open={genOpen}
        institution={institution}
        onClose={() => setGenOpen(false)}
        onDone={(created, skipped) => {
          setGenOpen(false);
          void qc.invalidateQueries({ queryKey: ['invoices', institution] });
          void qc.invalidateQueries({ queryKey: ['briefing', institution] });
          void message.success(`${created} invoice${created === 1 ? '' : 's'} created${skipped ? `, ${skipped} skipped` : ''}`);
        }}
      />
    </Space>
  );
}

function GenerateInvoicesModal({
  open, institution, onClose, onDone,
}: { open: boolean; institution: string; onClose: () => void; onDone: (created: number, skipped: number) => void }) {
  const cal = useQuery({
    queryKey: ['calendar', institution],
    queryFn: () => productApi.getCalendar(institution),
    enabled: !!institution && open,
  });
  const current = cal.data?.current_session ?? null;
  const noSession = cal.data && cal.data.sessions.length === 0;

  return (
    <FormModal<{ session_id: string; due_on: string }, { created: number; skipped_existing: number }>
      open={open} onClose={onClose}
      title="Generate invoices" submitText="Generate"
      initial={{ session_id: current?.id, due_on: new Date().toISOString().slice(0, 10) }}
      mutation={(v) => productApi.generateInvoices(institution, v)}
      onSuccess={(r) => onDone(r.created, r.skipped_existing)}
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12.5 }}>
        Creates one invoice per enrolled student × applicable fee head. Skips (student × head) pairs
        that already exist in this session — safe to re-run.
      </Typography.Paragraph>
      {noSession ? (
        <Alert type="warning" showIcon message="No academic session defined. Open Setup step 2 first." />
      ) : (
        <>
          <Field name="session_id" label="Session" required>
            <Select
              options={(cal.data?.sessions ?? []).map((s) => ({
                value: s.id,
                label: `${s.label}${s.is_current ? ' · current' : ''}`,
              }))}
            />
          </Field>
          <Field name="due_on" label="Due date" required>
            <DateField />
          </Field>
        </>
      )}
    </FormModal>
  );
}

function RecordReceiptModal({
  invoice, institution, onClose, onDone,
}: { invoice: InvoiceRow | null; institution: string; onClose: () => void; onDone: () => void }) {
  const owed = invoice ? invoice.amount_paise - invoice.paid_paise : 0;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <FormModal<{ amount_rupees: number; method: PaymentMethod; received_on: string; note?: string }>
      open={!!invoice} onClose={onClose}
      title="Record receipt" submitText="Record"
      initial={{ amount_rupees: owed / 100, method: 'upi', received_on: today }}
      mutation={(v) => productApi.createReceipt(institution, invoice!.id, {
        amount_paise: Math.round(v.amount_rupees * 100),
        method: v.method,
        received_on: v.received_on,
        note: v.note?.trim() || undefined,
      })}
      onSuccess={onDone}
    >
      {invoice && (
        <div style={{ marginBottom: 12, padding: 10, background: '#f7f7f5', borderRadius: 6 }}>
          <div style={{ fontWeight: 600 }}>{invoice.student_name} · {invoice.admission_number}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {invoice.fee_head_name} · owed <b>₹{paise(owed)}</b> · due {invoice.due_on}
          </Typography.Text>
        </div>
      )}
      <FormSection cols={2}>
        <Field name="amount_rupees" label="Amount" required rules={[V.money(0.01, owed / 100)]}>
          <MoneyField min={0.01} max={owed / 100} />
        </Field>
        <Field name="method" label="Method" required>
          <Select options={[
            { value: 'upi', label: 'UPI' },
            { value: 'cash', label: 'Cash' },
            { value: 'card', label: 'Card' },
            { value: 'bank_transfer', label: 'Bank transfer' },
            { value: 'cheque', label: 'Cheque' },
          ]} />
        </Field>
        <Field name="received_on" label="Received on" required>
          <DateField />
        </Field>
        <Field name="note" label="Note" optional>
          <TextField placeholder="Ref, UPI ID, cheque no." />
        </Field>
      </FormSection>
      <Typography.Text type="secondary" style={{ fontSize: 11.5 }}>
        Receipt number is issued by the server and can't be edited (M-01). A reversal receipt
        is the correction pattern — never a delete.
      </Typography.Text>
    </FormModal>
  );
}

function paise(n: number): string {
  return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
